import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, KeyRound, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function SchoolResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState({ pwd: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.pwd.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    if (form.pwd !== form.confirm) {
      toast.error("As senhas não conferem");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/school-reset-password", {
        token,
        new_password: form.pwd,
      });
      setDone(true);
      toast.success("Senha redefinida com sucesso");
      setTimeout(() => nav("/escola/login", { replace: true }), 2200);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Erro ao redefinir senha. Solicite um novo link.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl p-8 card-shadow">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-[#1e3a8a] flex items-center justify-center mb-3">
              <KeyRound className="w-8 h-8 text-white" strokeWidth={2.2} />
            </div>
            <h1 className="font-heading text-2xl font-black text-[#1e3a8a]">Nova senha</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-xs">
              Escolha uma nova senha para acessar sua conta.
            </p>
          </div>

          {done ? (
            <div
              className="rounded-2xl border border-green-200 bg-green-50 p-4 text-center"
              data-testid="reset-done-banner"
            >
              <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-2" />
              <p className="font-heading text-base font-bold text-green-900 mb-1">
                Senha atualizada
              </p>
              <p className="text-sm text-green-800">
                Redirecionando para o login...
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Nova senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type={show ? "text" : "password"}
                    value={form.pwd}
                    onChange={(e) => setForm({ ...form, pwd: e.target.value })}
                    data-testid="reset-pwd"
                    required
                    autoFocus
                    minLength={6}
                    className="w-full h-11 pl-9 pr-10 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent"
                    placeholder="Mínimo 6 caracteres"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    data-testid="reset-pwd-toggle"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={show ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Confirmar nova senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type={show ? "text" : "password"}
                    value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    data-testid="reset-confirm"
                    required
                    minLength={6}
                    className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent"
                    placeholder="Repita a senha"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={saving}
                className="w-full h-11 bg-[#7c3aed] hover:bg-[#6d28d9] text-white rounded-xl font-semibold"
                data-testid="reset-submit"
              >
                {saving ? "Salvando..." : "Salvar nova senha"}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/escola/login"
              data-testid="reset-back-login"
              className="text-xs text-slate-500 hover:text-[#1e3a8a]"
            >
              Voltar para o login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
